import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Box, Button, LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { ratingApiSlice } from '../../../features/rating/ratingSlice'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import { StudentRatingState, StudentSectionAnalysisState } from '../../../features/types'
import LevelCards from '../../curator/ratingPage/components/LevelCards'
import { LEVELS, LEVEL_COLORS, levelOf, levelStats, percentOf, studentLevel } from '../../curator/ratingPage/ratingLevel'
import { HeadFilters, groupInScope, useHeadFilters, useHeadIndex } from '../headData'
import { useDirectorSubject, useHeadScope } from '../headScope'
import { DirSubjectRow, dirSubjectsArgs, directorApi } from '../../../features/director/directorApi'

const LOW = 60
const LIST_SIZE = 6

type GroupStudent = StudentSectionAnalysisState & { groupId: number }

// Предыдущий период для стрелок ↑↓: прошлая неделя или прошлый месяц
const previousPeriod = (m?: number, w?: number) => {
    if (!m) return null
    if (w) return w > 1 ? { m, w: w - 1 } : m > 1 ? { m: m - 1, w: 4 } : null
    return m > 1 ? { m: m - 1, w: 0 } : null
}

const avg = (values: number[]) => (values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0)

const HeadOverviewPage = () => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const f = useHeadFilters()
    const index = useHeadIndex()
    const { basePath, director } = useHeadScope()
    const { subjectId } = useDirectorSubject()
    // сервис басшысы без выбранного пәна сравнивает пәндер, иначе - менторов
    const bySubject = !!director && !subjectId
    const prev = previousPeriod(f.monthNumber, f.weekNumber)

    const { data: rating, isFetching: ratingLoading } = ratingApiSlice.useGetStudentRatingQuery({
        groupId: f.groupId || 0,
        monthNumber: f.monthNumber || 0,
        weekNumber: f.weekNumber || 0,
        filter: '',
        page: 0,
        size: 1000,
    })
    // на «Барлық пәндер» KPI и сравнение берём из /director/subjects, анализ групп не нужен
    const { data: groupsNow = [] } = sectionApiSlice.useGetGroupsSectionAnalysisQuery(
        { curatorId: 0, monthNumber: f.monthNumber, weekNumber: f.weekNumber },
        { skip: bySubject },
    )
    const { data: groupsPrev = [] } = sectionApiSlice.useGetGroupsSectionAnalysisQuery(
        { curatorId: 0, monthNumber: prev?.m, weekNumber: prev?.w || undefined },
        { skip: !prev || bySubject },
    )
    const { data: weakTopics = [] } = curatorStudentsApiSlice.useFetchWeakTopicsQuery({
        courseId: f.courseId || 0,
        monthNumber: f.monthNumber || 0,
        weekNumber: f.weekNumber || 0,
    })

    const { data: dirSubjects = [], isFetching: dirSubjectsLoading } = directorApi.useGetSubjectsQuery(dirSubjectsArgs(f), { skip: !director })
    const { data: dirSubjectsPrev = [] } = directorApi.useGetSubjectsQuery(
        dirSubjectsArgs({ courseId: f.courseId, monthNumber: prev?.m, weekNumber: prev?.w }),
        { skip: !bySubject || !prev },
    )
    const { data: reminders } = directorApi.useGetRemindersQuery(undefined, { skip: !director })
    const subjectTitle = (row?: { titleKz: string; titleRu: string }) => (row ? row[`title${capitalize(locale)}` as 'titleKz'] || row.titleRu : '')

    // ментор -> его пәндер (для сравнения по пәндерам и подписи в списках)
    const [fetchSubjectMentors] = directorApi.useLazyGetSubjectMentorsQuery()
    const [mentorSubjects, setMentorSubjects] = useState<Map<number, number[]>>(new Map())
    useEffect(() => {
        if (!director || bySubject || !dirSubjects.length) return
        let cancelled = false
        Promise.all(
            dirSubjects.map((sj) =>
                fetchSubjectMentors({ subjectId: sj.subjectId, courseId: f.courseId || 0 }, true)
                    .unwrap()
                    .then((rows) => rows.map((r) => [r.mentorId, sj.subjectId] as const))
                    .catch(() => [] as (readonly [number, number])[]),
            ),
        ).then((lists) => {
            if (cancelled) return
            const map = new Map<number, number[]>()
            lists.flat().forEach(([m, sj]) => map.set(m, [...(map.get(m) ?? []), sj]))
            setMentorSubjects(map)
        })
        return () => {
            cancelled = true
        }
    }, [director, bySubject, dirSubjects.map((s) => s.subjectId).join(','), f.courseId])

    const ratingRows = useMemo(
        () => (rating?.data ?? []).filter((r) => groupInScope(index, f, r.groupId)),
        [rating, index, f.courseId, f.groupId],
    )
    const scopeGroups = useMemo(
        () => Array.from(index.groupTitle.keys()).filter((id) => groupInScope(index, f, id)),
        [index, f.courseId, f.groupId],
    )

    // Видео и тест по ученикам - из анализа групп (те же цифры, что на странице «Топтар»)
    const [fetchGroupStudents] = sectionApiSlice.useLazyGetStudentsSectionAnalysisByGroupQuery()
    const [students, setStudents] = useState<GroupStudent[]>([])
    const [studentsLoading, setStudentsLoading] = useState(false)
    useEffect(() => {
        // ~150 групп по 4-15 с каждая: на «Барлық пәндер» не грузим, детали - после выбора пәна
        if (bySubject) {
            setStudents([])
            setStudentsLoading(false)
            return
        }
        let cancelled = false
        setStudentsLoading(true)
        Promise.all(
            scopeGroups.map((groupId) =>
                fetchGroupStudents({ groupId, page: 0, size: 500, monthNumber: f.monthNumber, weekNumber: f.weekNumber }, true)
                    .unwrap()
                    .then((r) => r.data.map((s) => ({ ...s, groupId })))
                    .catch(() => [] as GroupStudent[]),
            ),
        ).then((lists) => {
            if (cancelled) return
            // ученик в нескольких группах басшы - берём одну строку
            const seen = new Set<number>()
            setStudents(lists.flat().filter((s) => (seen.has(s.userId) ? false : (seen.add(s.userId), true))))
            setStudentsLoading(false)
        })
        return () => {
            cancelled = true
        }
    }, [bySubject, scopeGroups.join(','), f.monthNumber, f.weekNumber])

    // KPI: среднее по группам, взвешенное числом учеников группы
    const studentsPerGroup = useMemo(() => {
        const map = new Map<number, number>()
        students.forEach((s) => map.set(s.groupId, (map.get(s.groupId) ?? 0) + 1))
        return map
    }, [students])
    const weighted = (rows: typeof groupsNow, key: 'videoPercentage' | 'testPercentage', groupIds = scopeGroups) => {
        let sum = 0
        let weight = 0
        rows.forEach((g) => {
            if (!groupIds.includes(g.groupId)) return
            const w = studentsPerGroup.get(g.groupId) ?? 1
            sum += (g[key] || 0) * w
            weight += w
        })
        return weight ? Math.round(sum / weight) : 0
    }
    // среднее по пәндерам, взвешенное числом учеников пәна
    const bySubjectAvg = (rows: DirSubjectRow[], key: 'videoPercent' | 'testPercent') => {
        const weight = rows.reduce((s, r) => s + (r.studentsTotal || 0), 0)
        return weight ? Math.round(rows.reduce((s, r) => s + (r[key] || 0) * (r.studentsTotal || 0), 0) / weight) : 0
    }
    const videoNow = bySubject ? bySubjectAvg(dirSubjects, 'videoPercent') : weighted(groupsNow, 'videoPercentage')
    const testNow = bySubject ? bySubjectAvg(dirSubjects, 'testPercent') : weighted(groupsNow, 'testPercentage')
    const videoPrev = !prev ? null : bySubject ? bySubjectAvg(dirSubjectsPrev, 'videoPercent') : weighted(groupsPrev, 'videoPercentage')
    const testPrev = !prev ? null : bySubject ? bySubjectAvg(dirSubjectsPrev, 'testPercent') : weighted(groupsPrev, 'testPercentage')
    const studentsCount = bySubject ? dirSubjects.reduce((s, r) => s + (r.studentsTotal || 0), 0) : students.length

    const scopedMentors = useMemo(
        () =>
            index.mentors
                .map((m) => ({ ...m, groupIds: m.groupIds.filter((id) => scopeGroups.includes(id)) }))
                .filter((m) => m.groupIds.length),
        [index, scopeGroups],
    )
    // единица сравнения: ментор или (у сервис басшысы на «Барлық пәндер») пән - тогда цифры готовые из /director/subjects
    const units = useMemo(
        () => (bySubject ? [] : scopedMentors.map((m) => ({ id: m.mentorId, name: m.name, groupIds: m.groupIds }))),
        [bySubject, scopedMentors],
    )
    const subjectRows = useMemo(() => dirSubjects.filter((sj) => sj.studentsTotal), [dirSubjects])

    const mentorVideo = useMemo(
        () =>
            (bySubject
                ? subjectRows.map((sj) => ({ mentorId: sj.subjectId, name: subjectTitle(sj), video: Math.round(sj.videoPercent || 0) }))
                : units.map((u) => ({ mentorId: u.id, name: u.name, video: weighted(groupsNow, 'videoPercentage', u.groupIds) }))
            ).sort((a, b) => b.video - a.video),
        [bySubject, subjectRows, units, groupsNow, studentsPerGroup, locale],
    )
    const subjectRating = useMemo(
        () =>
            subjectRows
                .map((sj) => ({ id: sj.subjectId, name: subjectTitle(sj), pct: Math.round(sj.ratingPercent || 0) }))
                .sort((a, b) => b.pct - a.pct),
        [subjectRows, locale],
    )

    const lowVideo = students.filter((s) => s.watchedVideoPercentage < LOW).sort((a, b) => a.watchedVideoPercentage - b.watchedVideoPercentage)
    const lowTest = students.filter((s) => s.topicTestPercentage < LOW).sort((a, b) => a.topicTestPercentage - b.topicTestPercentage)

    const stats = levelStats(ratingRows)
    // у ученика бывает несколько записей по предмету - в топах оставляем лучшую
    const rated = Array.from(
        ratingRows
            .filter((r) => r.maxPoint > 0)
            .sort((a, b) => percentOf(b.totalPoint, b.maxPoint) - percentOf(a.totalPoint, a.maxPoint))
            .reduce((map, r) => (map.has(r.userId) ? map : map.set(r.userId, r)), new Map<number, StudentRatingState>())
            .values(),
    )
    const top5 = rated.slice(0, 5)
    const bottom5 = rated.slice(-5).reverse()

    const mentorRating = useMemo(
        () =>
            units
                .map((u) => ({ mentorId: u.id, name: u.name, groupIds: u.groupIds }))
                .map((m) => {
                    const rows = ratingRows.filter((r) => r.groupId && m.groupIds.includes(r.groupId) && r.maxPoint > 0)
                    return { mentorId: m.mentorId, name: m.name, rows, stats: levelStats(rows) }
                })
                .filter((m) => m.rows.length)
                .map((m) => ({ ...m, avgPct: Math.round(avg(m.rows.map((r) => percentOf(r.totalPoint, r.maxPoint)))) }))
                .sort((a, b) => b.avgPct - a.avgPct),
        [units, ratingRows],
    )

    // сервис басшысы: менторы с самым низким видео/тестом, у каждого пән, топ, сколько учеников ниже 60%
    const lowMentors = (key: 'videoPercentage' | 'testPercentage', field: 'watchedVideoPercentage' | 'topicTestPercentage') =>
        scopedMentors
            .map((m) => ({
                mentorId: m.mentorId,
                name: m.name,
                groupIds: m.groupIds,
                pct: weighted(groupsNow, key, m.groupIds),
                low: students.filter((s) => m.groupIds.includes(s.groupId) && s[field] < LOW).length,
                subject: (mentorSubjects.get(m.mentorId) ?? [])
                    .map((id) => subjectTitle(dirSubjects.find((sj) => sj.subjectId === id)))
                    .filter(Boolean)
                    .join(', '),
            }))
            .sort((a, b) => a.pct - b.pct)
    const lowVideoMentors = director && !bySubject ? lowMentors('videoPercentage', 'watchedVideoPercentage') : []
    const lowTestMentors = director && !bySubject ? lowMentors('testPercentage', 'topicTestPercentage') : []
    const payLeks = (reminders?.reminders ?? []).filter((r) => r.type !== 'SOON').length

    const openStudent = (userCourseEnrollId?: number) => {
        if (userCourseEnrollId) navigate(`${basePath}/students/${userCourseEnrollId}`)
    }
    const openMentorStudents = (groupIds: number[]) => {
        const g = groupIds[0]
        navigate(`${basePath}/students?courseId=${index.groupCourse.get(g) ?? ''}&groupId=${g}`)
    }

    const loading = ratingLoading || studentsLoading || index.isFetching || (bySubject && dirSubjectsLoading)

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'headOverview' })}</H3>
                <Box sx={{ color: ui.sub, fontSize: '13.5px', margin: '6px 0 18px' }}>{formatMessage({ id: director ? 'dirOverviewSub' : 'headOverviewSub' })}</Box>

                <HeadFilters filters={f} index={index} />
                {loading && <LinearProgress sx={{ marginBottom: 2 }} />}

                <Box sx={kpiRow}>
                    <Kpi dark label={formatMessage({ id: 'headStudentsCount' })} value={`${studentsCount}`} />
                    <Kpi label={formatMessage({ id: 'headAvgVideo' })} value={`${videoNow}%`} delta={videoPrev == null ? null : videoNow - videoPrev} />
                    <Kpi label={formatMessage({ id: 'headAvgTest' })} value={`${testNow}%`} delta={testPrev == null ? null : testNow - testPrev} />
                </Box>

                {bySubject && (
                    <Box sx={{ ...hintBox, marginBottom: '18px' }}>{formatMessage({ id: 'dirOverviewPickSubject' })}</Box>
                )}

                <Box sx={grid}>
                    <Panel title={formatMessage({ id: 'headAttention' })}>
                        <AttentionRow
                            label={formatMessage({ id: bySubject ? 'dirLowestSubject' : 'headLowestMentor' })}
                            value={mentorVideo.length ? `${mentorVideo[mentorVideo.length - 1].name} · ${mentorVideo[mentorVideo.length - 1].video}%` : '-'}
                            tone="bad"
                        />
                        <AttentionRow
                            label={formatMessage({ id: bySubject ? 'dirHighestSubject' : 'headHighestMentor' })}
                            value={mentorVideo.length ? `${mentorVideo[0].name} · ${mentorVideo[0].video}%` : '-'}
                            tone="good"
                        />
                        {!bySubject && (
                            <>
                                <AttentionRow label={formatMessage({ id: 'headVideoBelow60' })} value={`${lowVideo.length}`} tone="bad" />
                                <AttentionRow label={formatMessage({ id: 'headTestBelow60' })} value={`${lowTest.length}`} tone="bad" />
                            </>
                        )}
                        {director && (
                            <AttentionRow
                                label={formatMessage({ id: 'dirPayLeks' })}
                                value={`${payLeks}`}
                                tone="warn"
                                onClick={() => navigate(`${basePath}/salary`)}
                            />
                        )}
                    </Panel>

                    <Panel title={formatMessage({ id: bySubject ? 'dirSubjectVideo' : 'headMentorVideo' })}>
                        {mentorVideo.length === 0 && <Empty />}
                        {mentorVideo.map((m) => (
                            <Box key={m.mentorId} sx={{ marginBottom: '10px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span>{m.name}</span>
                                    <b>{m.video}%</b>
                                </Box>
                                <Box sx={track}>
                                    <Box sx={{ height: '100%', width: `${Math.min(m.video, 100)}%`, background: ui.pink, borderRadius: 4 }} />
                                </Box>
                            </Box>
                        ))}
                    </Panel>
                </Box>

                <Panel
                    title={formatMessage({ id: 'headRatingAnalysis' })}
                    action={<Button size="small" onClick={() => navigate(`${basePath}/rating`)}>{formatMessage({ id: 'headGoToRating' })} →</Button>}
                >
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <LevelCards counts={stats.counts} total={stats.rated} hideCount />
                        <Box sx={{ flex: 1, minWidth: 280 }}>
                            {bySubject &&
                                subjectRating.map((s) => (
                                    <Box key={s.id} sx={{ marginBottom: '10px' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                            <span>{s.name}</span>
                                            <b style={{ color: LEVEL_COLORS[levelOf(s.pct)].text }}>{s.pct}%</b>
                                        </Box>
                                        <Box sx={track}>
                                            <Box sx={{ height: '100%', width: `${Math.min(s.pct, 100)}%`, background: ui.pink, borderRadius: 4 }} />
                                        </Box>
                                    </Box>
                                ))}
                            {mentorRating.map((m) => (
                                <Box key={m.mentorId} sx={{ marginBottom: '10px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                        <span>{m.name}</span>
                                        <b>{m.avgPct}%</b>
                                    </Box>
                                    <Box sx={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden', marginTop: '4px' }}>
                                        {LEVELS.map((level) => {
                                            const share = m.stats.rated ? Math.round((m.stats.counts[level] * 100) / m.stats.rated) : 0
                                            return share ? (
                                                <Box
                                                    key={level}
                                                    sx={{
                                                        width: `${share}%`,
                                                        background: LEVEL_COLORS[level].border,
                                                        color: LEVEL_COLORS[level].text,
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {share >= 8 ? `${share}%` : ''}
                                                </Box>
                                            ) : null
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ ...grid, marginTop: '18px' }}>
                        <RatingList title={formatMessage({ id: 'headTop5' })} rows={top5} index={index} onOpen={openStudent} />
                        <RatingList title={formatMessage({ id: 'headBottom5' })} rows={bottom5} index={index} onOpen={openStudent} />
                    </Box>
                </Panel>

                {director && !bySubject && (
                    <Box sx={grid}>
                        <MentorList
                            title={formatMessage({ id: 'dirLowVideoMentors' })}
                            rows={lowVideoMentors}
                            index={index}
                            footer={formatMessage({ id: 'dirVideoBelow65' }, { n: lowVideoMentors.filter((m) => m.pct < 65).length })}
                            onOpen={openMentorStudents}
                            onAll={() => navigate(`${basePath}/students`)}
                        />
                        <MentorList
                            title={formatMessage({ id: 'dirLowTestMentors' })}
                            rows={lowTestMentors}
                            index={index}
                            footer={formatMessage({ id: 'dirTestBelow60' }, { n: lowTestMentors.filter((m) => m.pct < LOW).length })}
                            onOpen={openMentorStudents}
                            onAll={() => navigate(`${basePath}/students`)}
                        />
                    </Box>
                )}

                {!bySubject && <Box sx={grid}>
                    <StudentList
                        title={formatMessage({ id: 'headLowVideoStudents' })}
                        rows={lowVideo.slice(0, LIST_SIZE)}
                        value={(s) => s.watchedVideoPercentage}
                        index={index}
                        onOpen={openStudent}
                    />
                    <StudentList
                        title={formatMessage({ id: 'headLowTestStudents' })}
                        rows={lowTest.slice(0, LIST_SIZE)}
                        value={(s) => s.topicTestPercentage}
                        index={index}
                        onOpen={openStudent}
                    />
                </Box>}

                <Panel title={formatMessage({ id: 'headWeakTopics' })}>
                    {weakTopics.length === 0 && <Empty />}
                    {weakTopics.map((t) => {
                        const pct = t.attempted ? Math.round((t.failed * 100) / t.attempted) : 0
                        return (
                            <Box key={t.topicId} sx={listRow}>
                                <span>{t[`title${capitalize(locale)}` as 'titleKz'] || t.titleRu}</span>
                                <Box sx={{ color: LEVEL_COLORS.red.text, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {formatMessage({ id: 'headFailedPercent' }, { pct })}
                                </Box>
                            </Box>
                        )
                    })}
                </Panel>
            </Box>
        </Box>
    )
}

const Kpi: React.FC<{ label: string; value: string; delta?: number | null; dark?: boolean }> = ({ label, value, delta, dark }) => {
    const { formatMessage } = useIntl()
    return (
        <Box sx={{ borderRadius: '16px', padding: '18px 20px', background: dark ? ui.pinkSoft : ui.card, border: `1px solid ${dark ? ui.pinkSoft : ui.border}` }}>
            <Box sx={{ fontSize: '12.5px', color: dark ? ui.pinkDark : ui.sub }}>{label}</Box>
            <Box sx={{ fontSize: '28px', fontWeight: 800, color: dark ? ui.pinkDark : ui.ink, marginTop: '6px' }}>{value}</Box>
            {delta != null && (
                <Box sx={{ fontSize: '12.5px', fontWeight: 700, marginTop: '4px', color: delta >= 0 ? LEVEL_COLORS.green.text : LEVEL_COLORS.red.text }}>
                    {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}% {formatMessage({ id: 'headVsPrevious' })}
                </Box>
            )}
        </Box>
    )
}

const Panel: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
    <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '14px', padding: '16px 18px', marginBottom: '18px', background: ui.card }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Box sx={{ fontWeight: 700, fontSize: '15px', color: ui.ink }}>{title}</Box>
            {action}
        </Box>
        {children}
    </Box>
)

const TONES = { good: LEVEL_COLORS.green, warn: LEVEL_COLORS.yellow, bad: LEVEL_COLORS.red }

const AttentionRow: React.FC<{ label: string; value: string; tone: keyof typeof TONES; onClick?: () => void }> = ({ label, value, tone, onClick }) => (
    <Box sx={{ ...listRow, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
        <span>{label}</span>
        <Box sx={{ fontWeight: 700, color: TONES[tone].text, textAlign: 'right' }}>{value}</Box>
    </Box>
)

const RatingList: React.FC<{
    title: string
    rows: StudentRatingState[]
    index: ReturnType<typeof useHeadIndex>
    onOpen: (id?: number) => void
}> = ({ title, rows, index, onOpen }) => {
    const { formatMessage } = useIntl()
    return (
        <Box>
            <Box sx={{ fontWeight: 700, fontSize: '13.5px', marginBottom: '8px' }}>{title}</Box>
            {rows.length === 0 && <Empty />}
            {rows.map((r) => {
                const level = studentLevel(r.totalPoint, r.maxPoint)
                const c = level ? LEVEL_COLORS[level] : null
                return (
                    <Box key={r.userSubjectEnrollId} sx={{ ...listRow, cursor: 'pointer' }} onClick={() => onOpen(r.userCourseEnrollId)}>
                        <Box>
                            <Box sx={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</Box>
                            <Box sx={{ fontSize: '12px', color: ui.muted }}>{index.mentorNames(r.groupId)}</Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
                            <span>{formatMessage({ id: 'headPoints' }, { points: r.totalPoint })}</span>
                            <Box sx={{ padding: '2px 8px', borderRadius: '100px', background: c?.bg, color: c?.text, fontWeight: 700, fontSize: '12px' }}>
                                {percentOf(r.totalPoint, r.maxPoint)}%
                            </Box>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

const StudentList: React.FC<{
    title: string
    rows: GroupStudent[]
    value: (s: GroupStudent) => number
    index: ReturnType<typeof useHeadIndex>
    onOpen: (id?: number) => void
}> = ({ title, rows, value, index, onOpen }) => (
    <Panel title={title}>
        {rows.length === 0 && <Empty />}
        {rows.map((s) => (
            <Box key={s.userId} sx={{ ...listRow, cursor: 'pointer' }} onClick={() => onOpen(s.userCourseEnrollId)}>
                <Box>
                    <Box sx={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</Box>
                    <Box sx={{ fontSize: '12px', color: ui.muted }}>
                        {index.groupTitle.get(s.groupId)} · {index.mentorNames(s.groupId)}
                    </Box>
                </Box>
                <Box sx={{ fontWeight: 700, color: LEVEL_COLORS.red.text }}>{Math.round(value(s))}%</Box>
            </Box>
        ))}
    </Panel>
)

interface LowMentor {
    mentorId: number
    name: string
    groupIds: number[]
    pct: number
    low: number
    subject: string
}

const MentorList: React.FC<{
    title: string
    rows: LowMentor[]
    index: ReturnType<typeof useHeadIndex>
    footer: string
    onOpen: (groupIds: number[]) => void
    onAll: () => void
}> = ({ title, rows, index, footer, onOpen, onAll }) => {
    const { formatMessage } = useIntl()
    return (
        <Panel title={title}>
            {rows.length === 0 && <Empty />}
            {rows.slice(0, LIST_SIZE).map((m) => {
                const tone = m.pct >= 85 ? TONES.good : m.pct >= 65 ? TONES.warn : TONES.bad
                return (
                    <Box key={m.mentorId} sx={{ ...listRow, cursor: 'pointer' }} onClick={() => onOpen(m.groupIds)} title={formatMessage({ id: 'dirOpenMentorStudents' })}>
                        <Box>
                            <Box sx={{ fontWeight: 600 }}>{m.name}</Box>
                            <Box sx={{ fontSize: '12px', color: ui.muted }}>
                                {m.subject && <b style={{ color: ui.ink }}>{m.subject} · </b>}
                                {m.groupIds.map((g) => index.groupTitle.get(g)).join(', ')} · {formatMessage({ id: 'dirStudentsBelow60' }, { n: m.low })}
                            </Box>
                        </Box>
                        <Box sx={{ padding: '2px 8px', borderRadius: '100px', background: tone.bg, color: tone.text, fontWeight: 700, fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {m.pct}%
                        </Box>
                    </Box>
                )
            })}
            <Box sx={{ ...listRow, borderBottom: 'none' }}>
                <Box sx={{ color: ui.muted }}>{footer}</Box>
                <Button size="small" onClick={onAll}>{formatMessage({ id: 'students' })} →</Button>
            </Box>
        </Panel>
    )
}

const Empty = () => {
    const { formatMessage } = useIntl()
    return <Box sx={{ color: ui.muted, fontSize: '13px' }}>{formatMessage({ id: 'noData' })}</Box>
}

const page = { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: '16px', padding: '24px', minHeight: '80vh' }
const hintBox = { background: tone.info.bg, border: `1px solid ${tone.info.border}`, color: tone.info.text, borderRadius: '12px', padding: '12px 16px', fontSize: '13.5px' }
const kpiRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, marginBottom: '18px' }
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 2 }
const track = { height: 8, background: ui.track, borderRadius: 4, marginTop: '4px', overflow: 'hidden' }
const listRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    padding: '8px 0',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: '13px',
    color: ui.ink,
}

export default HeadOverviewPage
